import React, { FC, useMemo } from 'react';
import { Alert, Col, Row } from 'reactstrap';
import { inAgeRange, ProductModel, ProductVisibility } from '../../shared/orders';
import { RaffleWithUserDetails } from '../../shared/orders/raffle';
import { useTranslation } from '../translations';
import { useConvention } from '../utils';
import { RegTypeSelect } from './RegTypeSelect';

interface ConventionRegTypeFormProps {
  readonly attendanceType?: ProductModel;
  readonly attendanceTypes: ProductModel[];
  readonly paidAttendanceType?: ProductModel;
  readonly raffle?: RaffleWithUserDetails;
  readonly bornAt: Date | 'child' | null;
  readonly onSelect: (productId: number) => void;
}

export const RegTypeSelectList: FC<ConventionRegTypeFormProps> = ({
  attendanceType,
  attendanceTypes: products,
  paidAttendanceType,
  raffle,
  onSelect,
  bornAt,
}) => {
  const { ts } = useTranslation();
  const convention = useConvention();

  const attendanceTypes = useMemo(() => {
    return products.filter((at) => {
      if (
        attendanceType &&
        attendanceType.visibility !== ProductVisibility.PUBLIC &&
        attendanceType.id !== at.id
      ) {
        return false;
      }

      if (paidAttendanceType?.id === at.id) {
        return true;
      }

      if (at.requiredGrants.length > 0) {
        return at.requiredGrants.some((t) => t.productId === paidAttendanceType?.id);
      }

      if (bornAt !== 'child' && !inAgeRange(at, bornAt, convention.startAt)) {
        return false;
      }

      if (bornAt === 'child' && at.maxAge && at.maxAge <= convention.childAgeThreshold) {
        return true;
      }

      const isSelectable = paidAttendanceType?.upgrades.some(({ id }) => id === at.id);
      return !paidAttendanceType || !attendanceType || attendanceType.id === at.id || isSelectable;
    });
  }, [
    products,
    attendanceType,
    paidAttendanceType,
    bornAt,
    convention.startAt,
    convention.childAgeThreshold,
  ]);

  const selectedTypeId = attendanceType ? attendanceType.id : (paidAttendanceType?.id ?? 0);

  return (
    <Col xs={12}>
      <Row className="justify-content-center" id="registrationTypeSelect">
        <Col lg={10} xs={12}>
          <Row>
            {attendanceTypes.map((at) => (
              <RegTypeSelect
                isPaid={at.id === paidAttendanceType?.id}
                isSelected={at.id === selectedTypeId}
                key={at.id}
                onSelect={onSelect}
                product={at}
                raffle={raffle}
              />
            ))}
            {attendanceType && attendanceType.visibility !== ProductVisibility.PUBLIC && (
              <Col className="mb-10" id="attendanceTypeSpecial" lg={6} xs={12}>
                <Alert color="warning">
                  {ts('reg_type_select_list.you_are_currently_assigned_a')}
                </Alert>
              </Col>
            )}
          </Row>
        </Col>
      </Row>
    </Col>
  );
};
